import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useEffect, useRef } from 'react'
import type { LayerNode, LayerType } from '../data/v1'

// ─── Tree utilities ─────────────────────────────────────────────────────────

export function findNode(nodes: LayerNode[], id: string): LayerNode | null {
	for (const n of nodes) {
		if (n.id === id) { return n }
		const found = findNode(n.children, id)
		if (found) { return found }
	}
	return null
}

export function hasDescendant(node: LayerNode, id: string): boolean {
	for (const c of node.children) {
		if (c.id === id || hasDescendant(c, id)) { return true }
	}
	return false
}

export function countDescendants(node: LayerNode): number {
	return node.children.reduce((s, c) => s + 1 + countDescendants(c), 0)
}

// ─── Drop target state ──────────────────────────────────────────────────────

/** Encodes the visual + logical drop target. slotKey is used for indicator highlighting. */
export type HotTarget = {
	slotKey: string
	parentId: string | null
	idx: number
} | null

export function slotKey(parentId: string | null, idx: number): string {
	return `${parentId ?? '∅'}-${idx}`
}

/** Placement validity — single source of truth. */
export function canDrop(dragType: LayerType, targetParentId: string | null): boolean {
	if (targetParentId === null) {
		return dragType !== 'substack'
	}
	return dragType !== 'backdrop' && dragType !== 'content'
}

// ─── DropSlot ───────────────────────────────────────────────────────────────

type DropSlotProps = {
	parentId: string | null
	idx: number
	draggingId: string | null
	draggingType: LayerType | null
	hotTarget: HotTarget
	biggerTarget?: boolean
}

export function DropSlot({ parentId, idx, draggingId, hotTarget, biggerTarget }: DropSlotProps) {
	const ref = useRef<HTMLDivElement>(null)
	const key = slotKey(parentId, idx)
	const isHot = hotTarget?.slotKey === key

	useEffect(() => {
		const el = ref.current
		if (!el || !draggingId) { return }
		return dropTargetForElements({
			element: el,
			getData: () => ({ _kind: 'slot', parentId, idx, slotKey: key }),
			canDrop: ({ source }) => {
				const dragType = source.data.type as LayerType
				if (!canDrop(dragType, parentId)) { return false }
				if (parentId === (source.data.id as string)) { return false }
				return true
			},
		})
	}, [parentId, idx, key, draggingId])

	return (
		<div ref={ref} className={`relative ${biggerTarget ? 'h-4' : 'h-1'}`} data-is="drop-slot">
			{isHot && (
				<div className="pointer-events-none absolute inset-y-0 left-1.5 right-1 z-20 flex items-center">
					<div className="h-0.5 w-full rounded-full bg-blue-500 shadow-[0_0_8px_#3b82f699]">
						<div className="absolute -left-1 top-1/2 size-2 -translate-y-1/2 rounded-full bg-blue-500" />
					</div>
				</div>
			)}
		</div>
	)
}
